import type { SaveStore } from "@highmarch/app";
import type { SaveBackend, SaveMirror } from "./backends";
import { SaveBackendError, SaveCorruptError, SaveLockedError, SaveProblem, SaveQuotaError, SaveTooLargeError, isQuotaError } from "./saveErrors";
import type { WriterLock } from "./writerLock";

/**
 * The save store (FINISH_PLAN WP4.1, X4): the `SaveStore` port kept in two copies, a durable backend (IndexedDB on the
 * web, Preferences in the iOS shell) and a synchronous localStorage mirror, so neither a full localStorage, nor
 * WebKit dropping one of them, nor a page killed mid-write loses the save.
 *
 * - `open` reads both copies before the game loads the save (the port is synchronous, the backend is not). Each write
 *   carries a sequence number; the newer valid copy wins and the other is brought up to date. A save that only exists
 *   in localStorage (every save before this store) is copied into the backend: that is the migration.
 * - The backend copy carries a checksum. A copy that fails it, or is not JSON, is a `SaveCorruptError`: it is kept
 *   under `<key>.corrupt-<copy>` and the other copy is used. When the save itself fails validation, SaveService keeps
 *   it under its backup key; the store reports that as a `SaveCorruptError` too.
 * - The size guard: past `SaveSizeLimits.hardMaxChars` the `shrink` hook may drop what can be lost (the suspended
 *   battle's log, openSaveStore.ts); otherwise the write is refused and the last good save stays.
 * - A full device is a `SaveQuotaError`; a tab that lost the single-writer lock (writerLock.ts) stops writing.
 * Problems never throw into the game: they go to `onProblem` listeners, and `problems` keeps the ones seen so far.
 */

/** Characters (UTF-16 code units, what localStorage counts) past which the save is watched, mirrored or refused. */
export const SaveSizeLimits = {
  /** A normal save is tens of KB; past this, diagnostics notes the size. */
  warnChars: 256 * 1024,
  /** localStorage holds ~5 MB per origin (2.5 M characters in WebKit): leave room for settings and the backup. */
  mirrorMaxChars: 2 * 1024 * 1024,
  /** Nothing the game keeps should come near this; past it the save is shrunk or refused. */
  hardMaxChars: 4 * 1024 * 1024,
} as const;

/** How the durable copy is stored: the save JSON with its sequence number and checksum. */
interface Envelope { readonly v: 1; readonly seq: number; readonly sum: string; readonly json: string }
const ENVELOPE_VERSION = 1;

export interface DurableSaveStoreOptions {
  readonly backend: SaveBackend;
  /** null when localStorage is denied: the backend alone keeps the save. */
  readonly mirror: SaveMirror | null;
  /** The keys read at open (the save and its backup); any other key is read from the mirror on demand. */
  readonly keys: readonly string[];
  /** SaveService's backup key: a write to it means the save failed validation. */
  readonly corruptBackupKey?: string;
  readonly lock?: WriterLock;
  readonly limits?: typeof SaveSizeLimits;
  /** Makes an oversized save fit by dropping what can be lost; returns the smaller JSON and what it dropped, or null. */
  readonly shrink?: (key: string, json: string) => { json: string; dropped: string } | null;
  readonly log?: (level: "info" | "warning" | "error", message: string) => void;
}

/** FNV-1a over the UTF-16 code units, as 8 hex digits: catches truncation and bit rot, not tampering. */
export function checksum(s: string): string {
  const FnvOffset = 0x811c9dc5, FnvPrime = 0x01000193;
  let h = FnvOffset;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, FnvPrime) >>> 0; }
  return h.toString(16).padStart(8, "0");
}

export class DurableSaveStore implements SaveStore {
  /** Mirror keys match the old LocalStorageSaveStore's, so a save from before this store is found as it is. */
  static readonly MirrorPrefix = "highmarch.";
  static readonly SeqSuffix = ".seq";
  /** How many problems `problems` remembers (diagnostics shows them). */
  static readonly ProblemHistory = 20;

  private readonly cache = new Map<string, string | null>();
  private readonly seq = new Map<string, number>();
  private readonly listeners: Array<(p: SaveProblem) => void> = [];
  private readonly seen: SaveProblem[] = [];
  private pending: Promise<void> = Promise.resolve();
  private lockReported = false;
  /** Set by `freeze`: the page is reloading onto what was written so far. */
  private frozen = false;
  private readonly limits: typeof SaveSizeLimits;

  private constructor(private readonly o: DurableSaveStoreOptions) {
    this.limits = o.limits ?? SaveSizeLimits;
    o.lock?.setFlush(() => this.flush());
    o.lock?.onLost(() => this.problem(new SaveLockedError()));
  }

  /** Reads and reconciles both copies of every key in `keys`. */
  static async open(o: DurableSaveStoreOptions): Promise<DurableSaveStore> {
    const store = new DurableSaveStore(o);
    for (const key of o.keys) await store.hydrate(key);
    return store;
  }

  get backendName(): string { return this.o.backend.name; }
  get problems(): readonly SaveProblem[] { return this.seen; }
  onProblem(listener: (p: SaveProblem) => void): () => void {
    this.listeners.push(listener);
    return () => { const i = this.listeners.indexOf(listener); if (i >= 0) this.listeners.splice(i, 1); };
  }

  /** Resolves when every write so far has reached the backend (or failed). */
  flush(): Promise<void> { return this.pending; }

  /**
   * The page is about to reload onto the save as written so far (Settings → Reset save, a save import): later writes
   * are dropped, so the pagehide handlers cannot store the old save held in memory over the reset or the import.
   */
  freeze(): void { this.frozen = true; }

  /** Whether a write made now would be kept: false in a tab that lost the writer lock, or once frozen. */
  get writable(): boolean { return !this.frozen && (!this.o.lock || this.o.lock.isWriter); }

  /** The stored size of `key`, for diagnostics. */
  sizeOf(key: string): number { return this.load(key)?.length ?? 0; }

  /**
   * Another tab wrote one of the open keys since this one last read or wrote it (the mirror's sequence number is
   * ahead): this tab's copy of the save in memory is out of date. A page back from the back-forward cache asks.
   */
  isStale(): boolean {
    for (const key of this.o.keys) {
      const mirrorSeq = Number(this.mirrorTry((m) => m.getItem(DurableSaveStore.MirrorPrefix + key + DurableSaveStore.SeqSuffix)) ?? 0) || 0;
      if (mirrorSeq > (this.seq.get(key) ?? 0)) return true;
    }
    return false;
  }

  load(key: string): string | null {
    if (this.cache.has(key)) return this.cache.get(key) ?? null;
    return this.mirrorGet(key);
  }

  save(key: string, json: string): void {
    if (!this.mayWrite()) return;
    if (key === this.o.corruptBackupKey) this.problem(new SaveCorruptError("record", key, "the save failed validation"));
    let body = json;
    if (body.length > this.limits.hardMaxChars) {
      const smaller = this.o.shrink?.(key, body) ?? null;
      const fits = smaller !== null && smaller.json.length <= this.limits.hardMaxChars;
      this.problem(new SaveTooLargeError(body.length, this.limits.hardMaxChars, fits ? smaller.dropped : null));
      if (!fits) return;
      body = smaller.json;
    }
    if (body.length > this.limits.warnChars) this.o.log?.("warning", `save "${key}" is ${body.length} characters`);
    const seq = (this.seq.get(key) ?? 0) + 1;
    this.seq.set(key, seq);
    this.cache.set(key, body);
    this.writeMirror(key, body, seq);
    this.writeBackend(key, body, seq);
  }

  /**
   * Removes both copies. The mirror keeps the new sequence number with no value, a tombstone: the backend's delete is
   * only queued, and a reload straight after (Settings → Reset save) can abort it, so the next open must know the
   * backend's copy is older than the delete rather than let it win as the only copy left.
   */
  delete(key: string): void {
    if (!this.mayWrite()) return;
    const seq = (this.seq.get(key) ?? 0) + 1;
    this.cache.set(key, null);
    this.seq.set(key, seq);
    this.mirrorTry((m) => { m.removeItem(DurableSaveStore.MirrorPrefix + key); m.setItem(DurableSaveStore.MirrorPrefix + key + DurableSaveStore.SeqSuffix, String(seq)); });
    this.queue(() => this.o.backend.delete(key));
  }

  // ---------------------------------------------------------------- open

  private async hydrate(key: string): Promise<void> {
    const mirrorJson = this.mirrorGet(key);
    const mirrorSeq = Number(this.mirrorTry((m) => m.getItem(DurableSaveStore.MirrorPrefix + key + DurableSaveStore.SeqSuffix)) ?? 0) || 0;
    let raw: string | null = null;
    try { raw = await this.o.backend.get(key); } catch (err) { this.o.log?.("warning", `save backend read failed: ${(err as Error).message}`); }
    const backend = raw === null ? null : this.unwrap(key, raw);
    const mirrorOk = mirrorJson !== null && isJson(mirrorJson);
    if (mirrorJson !== null && !mirrorOk) {
      const backupKey = `${key}.corrupt-mirror`;
      this.queue(() => this.o.backend.set(backupKey, mirrorJson));
      this.problem(new SaveCorruptError("mirror", backupKey, "not JSON"));
    }
    // A tombstone (delete): the mirror's sequence number with no value, ahead of the backend's copy, which a reload
    // kept from being deleted. The delete wins and is finished now.
    if (mirrorJson === null && backend && mirrorSeq > backend.seq) {
      this.cache.set(key, null);
      this.seq.set(key, mirrorSeq);
      this.queue(() => this.o.backend.delete(key));
      return;
    }
    // The newer valid copy wins; a legacy mirror (no sequence) loses to any backend copy but beats none.
    let json: string | null, seq: number;
    if (backend && (!mirrorOk || backend.seq >= mirrorSeq)) ({ json, seq } = backend);
    else if (mirrorOk) { json = mirrorJson; seq = mirrorSeq; }
    // Neither copy is readable: hand the mirror's text on, so SaveService backs it up and starts afresh.
    else { json = mirrorJson; seq = Math.max(mirrorSeq, backend?.seq ?? 0); }
    this.cache.set(key, json);
    this.seq.set(key, seq);
    if (json === null) return;
    if (!backend || backend.seq !== seq || backend.json !== json) {
      if (!backend && raw === null) this.o.log?.("info", `save "${key}": copied into ${this.o.backend.name}`);
      this.writeBackend(key, json, seq);
    }
    if (mirrorOk && (mirrorJson !== json || mirrorSeq !== seq)) this.writeMirror(key, json, seq);
  }

  /** The backend's envelope, checked; a bad one is kept aside and reported. */
  private unwrap(key: string, raw: string): { json: string; seq: number } | null {
    let detail: string;
    try {
      const e = JSON.parse(raw) as Partial<Envelope>;
      if (e.v !== ENVELOPE_VERSION || typeof e.json !== "string" || !Number.isInteger(e.seq)) detail = "unknown envelope";
      else if (checksum(e.json) !== e.sum) detail = "checksum mismatch";
      else if (!isJson(e.json)) detail = "not JSON";
      else return { json: e.json, seq: e.seq as number };
    } catch { detail = "unreadable envelope"; }
    const backupKey = `${key}.corrupt-backend`;
    this.queue(() => this.o.backend.set(backupKey, raw));
    this.problem(new SaveCorruptError("backend", backupKey, detail));
    return null;
  }

  // ---------------------------------------------------------------- writes

  private mayWrite(): boolean {
    if (this.frozen) return false;
    if (!this.o.lock || this.o.lock.isWriter) return true;
    if (!this.lockReported) { this.lockReported = true; this.o.log?.("warning", "save write skipped: another tab is the writer"); }
    return false;
  }

  private writeMirror(key: string, json: string, seq: number): void {
    const m = this.o.mirror;
    if (!m) return;
    const k = DurableSaveStore.MirrorPrefix + key;
    if (json.length > this.limits.mirrorMaxChars) {
      // Too big to mirror: remove the stale copy so it cannot win at the next open (the backend has the save).
      this.mirrorTry((mm) => { mm.removeItem(k); mm.removeItem(k + DurableSaveStore.SeqSuffix); });
      return;
    }
    try {
      m.setItem(k, json);
      m.setItem(k + DurableSaveStore.SeqSuffix, String(seq));
    } catch (err) {
      if (isQuotaError(err)) this.problem(new SaveQuotaError("mirror", json.length));
      else this.o.log?.("warning", `save mirror write failed: ${(err as Error).message}`);
    }
  }

  private writeBackend(key: string, json: string, seq: number): void {
    const envelope: Envelope = { v: ENVELOPE_VERSION, seq, sum: checksum(json), json };
    const text = JSON.stringify(envelope);
    this.queue(() => this.o.backend.set(key, text), (err) => this.problem(isQuotaError(err) ? new SaveQuotaError("backend", text.length) : new SaveBackendError((err as Error)?.message ?? String(err))));
  }

  /** Backend writes run one at a time, in order, so a slow one is never overtaken by an older one. */
  private queue(op: () => Promise<void>, onError?: (err: unknown) => void): void {
    this.pending = this.pending.then(op).catch((err) => { if (onError) onError(err); else this.o.log?.("warning", `save backend: ${(err as Error)?.message ?? err}`); });
  }

  private mirrorGet(key: string): string | null {
    return this.mirrorTry((m) => m.getItem(DurableSaveStore.MirrorPrefix + key)) ?? null;
  }

  private mirrorTry<T>(f: (m: SaveMirror) => T): T | undefined {
    if (!this.o.mirror) return undefined;
    try { return f(this.o.mirror); } catch { return undefined; }
  }

  private problem(p: SaveProblem): void {
    this.seen.push(p);
    if (this.seen.length > DurableSaveStore.ProblemHistory) this.seen.shift();
    this.o.log?.(p.kind === "locked" ? "warning" : "error", p.message);
    for (const l of this.listeners) l(p);
  }
}

function isJson(s: string): boolean {
  try { JSON.parse(s); return true; } catch { return false; }
}
